import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import InlineFeedback from '../components/InlineFeedback';

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase && envBase.trim()) {
    return envBase.trim().replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const currentHost = window.location.hostname;
    if (currentHost === 'localhost' || currentHost === '127.0.0.1' || currentHost === '::1' || currentHost === '[::1]') {
      return 'http://127.0.0.1:8000/api';
    }
    return `http://${currentHost}:8000/api`;
  }

  return 'http://127.0.0.1:8000/api';
};

const API_BASE = resolveApiBaseUrl();
const apiUrl = (path = '') => `${API_BASE}/${String(path).replace(/^\/+/, '')}`;
const OPTION_INPUT_TYPES = ['radio', 'multiselect', 'dropdown', 'color'];
const PRICED_SELECTOR_TYPES = [...OPTION_INPUT_TYPES, 'range', 'toggle', 'checkbox'];

const ATTRIBUTE_TYPES = [
  { value: 'text', icon: 'Aa', label: 'Short answer', description: 'Names or brief notes' },
  { value: 'textarea', icon: '¶', label: 'Long answer', description: 'Detailed requests' },
  { value: 'number', icon: '#', label: 'Number', description: 'A numeric value' },
  { value: 'range', icon: '↔', label: 'Slider', description: 'Choose within a range' },
  { value: 'color', icon: '●', label: 'Color palette', description: 'Visual color swatches' },
  { value: 'toggle', icon: '◉', label: 'Yes / No', description: 'Simple toggle' },
  { value: 'radio', icon: '◌', label: 'Choice cards', description: 'Choose one option' },
  { value: 'multiselect', icon: '☑', label: 'Add-ons', description: 'Choose several options' },
  { value: 'dropdown', icon: '⌄', label: 'Dropdown', description: 'Compact option list' },
];

const EMPTY_SERVICE = {
  name: '',
  description: '',
  price: '',
  duration: '',
  category: '',
  shared: true,
  image_data: '',
};

const EMPTY_ATTRIBUTE = {
  name: '',
  input_type: '',
  required: false,
  help_text: '',
  placeholder: '',
  settings: {},
  options: [],
};

const normalizeOption = (option) => (
  typeof option === 'object'
    ? {
      id: option.id,
      value: option.value || '',
      color: option.color || '#6D3B7E',
      extra_price: option.extra_price ?? '0.00',
      extra_duration_minutes: option.extra_duration_minutes ?? 0,
    }
    : { value: option || '', color: '#6D3B7E', extra_price: '0.00', extra_duration_minutes: 0 }
);

const buildRangeOptions = (attribute) => {
  const minimum = Number(attribute.settings?.min ?? 0);
  const maximum = Number(attribute.settings?.max ?? 10);
  const step = Number(attribute.settings?.step ?? 1);
  if (!Number.isFinite(minimum) || !Number.isFinite(maximum) || !Number.isFinite(step) || step <= 0 || maximum <= minimum) return [];
  const existing = new Map((attribute.options || []).map(normalizeOption).map((option) => [String(option.value), option]));
  const values = [];
  for (let current = minimum, count = 0; current <= maximum + (step / 1000) && count < 100; current += step, count += 1) {
    const value = String(Number(current.toFixed(6)));
    values.push(existing.get(value) || normalizeOption(value));
  }
  return values;
};

const normalizeAttribute = (attribute) => {
  const normalized = {
    id: attribute.id,
    name: attribute.name || '',
    input_type: attribute.input_type || '',
    required: Boolean(attribute.required),
    help_text: attribute.help_text || '',
    placeholder: attribute.placeholder || '',
    settings: attribute.settings || {},
    options: (attribute.options || []).map(normalizeOption),
  };
  if (normalized.input_type === 'range') normalized.options = buildRangeOptions(normalized);
  if (['toggle', 'checkbox'].includes(normalized.input_type) && !normalized.options.length) {
    normalized.options = [normalizeOption({ value: 'Yes' })];
  }
  return normalized;
};

const optimizeImage = (file) => new Promise((resolve, reject) => {
  if (!file?.type?.startsWith('image/')) {
    reject(new Error('Choose a JPEG, PNG, or WebP image.'));
    return;
  }

  const reader = new FileReader();
  reader.onerror = () => reject(new Error('The image could not be read.'));
  reader.onload = () => {
    const image = new Image();
    image.onerror = () => reject(new Error('The image could not be processed.'));
    image.onload = () => {
      const maxDimension = 1400;
      const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.82);

      if (dataUrl.length > 4.1 * 1024 * 1024) {
        reject(new Error('This image is still too large after optimization. Try a smaller image.'));
        return;
      }
      resolve(dataUrl);
    };
    image.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const extractRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const StepIndicator = ({ currentStep }) => {
  const steps = [
    { id: 1, label: 'Basics' },
    { id: 2, label: 'Pricing' },
    { id: 3, label: 'Attributes' },
  ];

  return (
    <ol className="grid grid-cols-3 gap-2" aria-label="Service form progress">
      {steps.map((step) => {
        const isActive = currentStep === step.id;
        const isDone = currentStep > step.id;
        return (
          <li key={step.id}>
            <div
              className={`rounded-xl border px-3 py-2 text-sm font-medium text-center transition ${
                isActive
                  ? 'border-[#6D3B7E] bg-[#F6F0FA] text-[#4B2A56]'
                  : isDone
                    ? 'border-green-200 bg-green-50 text-green-800'
                    : 'border-gray-200 bg-white text-gray-600'
              }`}
            >
              {isDone ? '✓ ' : ''}
              {step.label}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

const WorkerServices = () => {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [profile, setProfile] = useState(null);
  const [myServices, setMyServices] = useState([]);
  const [libraryServices, setLibraryServices] = useState([]);
  const [attributesByService, setAttributesByService] = useState({});

  const [activeTab, setActiveTab] = useState('my');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  const [step, setStep] = useState(1);
  const [newService, setNewService] = useState(EMPTY_SERVICE);
  const [attributes, setAttributes] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [isProcessingImage, setIsProcessingImage] = useState(false);

  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isCloningId, setIsCloningId] = useState(null);
  const [feedback, setFeedback] = useState({ type: 'info', message: '' });

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const getCategoryId = (categoryValue) => {
    if (typeof categoryValue === 'object' && categoryValue !== null) return Number(categoryValue.id);
    return Number(categoryValue);
  };

  const categoryNameById = useMemo(
    () =>
      categories.reduce((acc, cat) => {
        acc[String(cat.id)] = cat.name;
        return acc;
      }, {}),
    [categories]
  );

  const getCategoryName = (service) => {
    if (service?.category_name) return service.category_name;
    if (typeof service?.category === 'object' && service?.category?.name) return service.category.name;
    return categoryNameById[String(service?.category)] || 'Uncategorized';
  };

  const getAllData = useCallback(async () => {
    if (!token) return;

    try {
      setIsLoadingPage(true);
      setFeedback({ type: 'info', message: '' });

      const [profileRes, categoriesRes, servicesRes] = await Promise.all([
        axios.get(apiUrl('userprofiles/me/'), { headers: authHeaders }),
        axios.get(apiUrl('categories/'), { headers: authHeaders }),
        axios.get(apiUrl('services/'), { headers: authHeaders }),
      ]);

      const profile = profileRes.data;
      const allServices = extractRows(servicesRes.data);
      const ownerCompanyId = profile?.role === 'owner' ? profile?.company?.id : null;
      const belongsToManagedCatalog = (service) => {
        if (service?.created_by?.id === profile?.id) return true;
        return Boolean(
          ownerCompanyId
          && service?.created_by?.company?.id === ownerCompanyId
        );
      };

      const mine = allServices.filter(belongsToManagedCatalog);
      const library = allServices.filter((service) => !belongsToManagedCatalog(service));

      setProfile(profile);
      setCategories(extractRows(categoriesRes.data));
      setMyServices(mine);
      setLibraryServices(library);
    } catch (error) {
      console.error('Failed loading worker services page data:', error);
      setFeedback({ type: 'error', message: 'Unable to load your services right now. Please refresh and try again.' });
    } finally {
      setIsLoadingPage(false);
    }
  }, [authHeaders, token]);

  const isOwner = profile?.role === 'owner';

  useEffect(() => {
    getAllData();
  }, [getAllData]);

  const resetCreateForm = () => {
    setNewService(EMPTY_SERVICE);
    setAttributes([]);
    setValidationErrors({});
    setEditingServiceId(null);
    setStep(1);
  };

  const fetchAttributesForService = async (serviceId) => {
    if (attributesByService[serviceId]) return;

    try {
      const attrRes = await axios.get(`${apiUrl('attributes/')}?service=${serviceId}`, {
        headers: authHeaders,
      });
      setAttributesByService((prev) => ({ ...prev, [serviceId]: extractRows(attrRes.data) }));
    } catch (error) {
      console.error('Error loading attributes for service:', serviceId, error);
      setFeedback({ type: 'warning', message: 'Could not load some service details.' });
    }
  };

  const setServiceField = (field, value) => {
    setNewService((prev) => ({ ...prev, [field]: value }));
    setValidationErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const addAttribute = () => {
    setAttributes((prev) => [...prev, { ...EMPTY_ATTRIBUTE, settings: {}, options: [] }]);
  };

  const updateAttribute = (index, key, value) => {
    setAttributes((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [key]: value };
      if (key === 'input_type') {
        if (!PRICED_SELECTOR_TYPES.includes(value)) next[index].options = [];
        if (value === 'range') {
          next[index].settings = { min: 0, max: 10, step: 1, unit: '' };
          next[index].options = buildRangeOptions(next[index]);
        } else if (['toggle', 'checkbox'].includes(value)) {
          next[index].options = [normalizeOption({ value: 'Yes' })];
        }
      }
      return next;
    });
  };

  const updateAttributeSetting = (index, key, value) => {
    setAttributes((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        settings: { ...(next[index].settings || {}), [key]: value },
      };
      if (next[index].input_type === 'range') next[index].options = buildRangeOptions(next[index]);
      return next;
    });
  };

  const removeAttribute = (index) => {
    setAttributes((prev) => prev.filter((_, idx) => idx !== index));
  };

  const addOption = (attributeIndex) => {
    setAttributes((prev) => {
      const next = [...prev];
      next[attributeIndex] = {
        ...next[attributeIndex],
        options: [
          ...(next[attributeIndex].options || []),
          normalizeOption({ value: '' }),
        ],
      };
      return next;
    });
  };

  const updateOption = (attributeIndex, optionIndex, field, value) => {
    setAttributes((prev) => {
      const next = [...prev];
      const options = (next[attributeIndex].options || []).map(normalizeOption);
      options[optionIndex] = { ...options[optionIndex], [field]: value };
      next[attributeIndex] = { ...next[attributeIndex], options };
      return next;
    });
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      setIsProcessingImage(true);
      setValidationErrors((prev) => ({ ...prev, image_data: '' }));
      const imageData = await optimizeImage(file);
      setServiceField('image_data', imageData);
    } catch (error) {
      setValidationErrors((prev) => ({ ...prev, image_data: error.message }));
    } finally {
      setIsProcessingImage(false);
    }
  };

  const removeOption = (attributeIndex, optionIndex) => {
    setAttributes((prev) => {
      const next = [...prev];
      const options = [...(next[attributeIndex].options || [])];
      options.splice(optionIndex, 1);
      next[attributeIndex] = { ...next[attributeIndex], options };
      return next;
    });
  };

  const validateStep = (targetStep) => {
    const errors = {};

    if (targetStep >= 1) {
      if (!newService.name.trim()) errors.name = 'Service name is required.';
      if (!newService.description.trim()) errors.description = 'Description helps clients understand your service.';
      if (!newService.category) errors.category = 'Please choose a category.';
    }

    if (targetStep >= 2) {
      const numericPrice = Number(newService.price);
      const numericDuration = Number(newService.duration);
      if (!Number.isFinite(numericPrice) || numericPrice <= 0) errors.price = 'Price must be greater than 0.';
      if (!Number.isFinite(numericDuration) || numericDuration <= 0) errors.duration = 'Duration must be greater than 0 minutes.';
    }

    if (targetStep >= 3) {
      attributes.forEach((attribute, index) => {
        if (!attribute.name?.trim()) errors[`attribute-name-${index}`] = 'Attribute name is required.';
        if (!attribute.input_type?.trim()) errors[`attribute-type-${index}`] = 'Choose an input type.';

        if (PRICED_SELECTOR_TYPES.includes(attribute.input_type)) {
          const normalizedOptions = attribute.input_type === 'range'
            ? buildRangeOptions(attribute)
            : (attribute.options || []).map(normalizeOption);
          const options = (attribute.options || [])
            .map(normalizeOption)
            .map((option) => option.value.trim())
            .filter(Boolean);
          if (!(normalizedOptions.length || options.length)) {
            errors[`attribute-options-${index}`] = 'Add at least one option value.';
          }
          normalizedOptions.forEach((option, optionIndex) => {
            if (Number(option.extra_price) < 0) errors[`attribute-option-price-${index}-${optionIndex}`] = 'Extra charge cannot be negative.';
            if (Number(option.extra_duration_minutes) < 0) errors[`attribute-option-duration-${index}-${optionIndex}`] = 'Extra time cannot be negative.';
          });
        }

        if (attribute.input_type === 'range') {
          const minimum = Number(attribute.settings?.min);
          const maximum = Number(attribute.settings?.max);
          const increment = Number(attribute.settings?.step);
          if (!Number.isFinite(minimum) || !Number.isFinite(maximum) || maximum <= minimum) {
            errors[`attribute-range-${index}`] = 'Maximum must be greater than minimum.';
          }
          if (!Number.isFinite(increment) || increment <= 0) {
            errors[`attribute-range-${index}`] = 'Step must be greater than zero.';
          }
        }
      });
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const goToNextStep = () => {
    if (validateStep(step) && step < 3) setStep((prev) => prev + 1);
  };

  const goToPreviousStep = () => {
    setStep((prev) => Math.max(1, prev - 1));
  };

  const handleUseAsTemplate = async (serviceId) => {
    try {
      const [serviceRes, attributesRes] = await Promise.all([
        axios.get(apiUrl(`services/${serviceId}/`), { headers: authHeaders }),
        axios.get(`${apiUrl('attributes/')}?service=${serviceId}`, { headers: authHeaders }),
      ]);

      const service = serviceRes.data;
      const fetchedAttributes = extractRows(attributesRes.data).map(normalizeAttribute);

      setNewService({
        name: service.name || '',
        description: service.description || '',
        price: service.price || '',
        duration: service.duration || '',
        category: getCategoryId(service.category) || '',
        shared: true,
        image_data: service.image_data || '',
      });
      setEditingServiceId(null);
      setAttributes(fetchedAttributes);
      setStep(1);
      setActiveTab('create');
      setValidationErrors({});
      setFeedback({ type: 'info', message: 'Template loaded. Review and save as your own service.' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error loading template service:', error);
      setFeedback({ type: 'error', message: 'Could not load template details.' });
    }
  };

  const handleEditService = async (service) => {
    const serviceId = service.id;
    try {
      const attributesRes = await axios.get(`${apiUrl('attributes/')}?service=${serviceId}`, {
        headers: authHeaders,
      });
      const editableAttributes = extractRows(attributesRes.data).map(normalizeAttribute);

      setNewService({
        name: service.name || '',
        description: service.description || '',
        price: service.price || '',
        duration: service.duration || '',
        category: getCategoryId(service.category) || '',
        shared: Boolean(service.shared),
        image_data: service.image_data || '',
      });
      setAttributes(editableAttributes);
      setEditingServiceId(serviceId);
      setValidationErrors({});
      setStep(1);
      setActiveTab('create');
      setFeedback({ type: 'info', message: `Editing “${service.name}”. Your changes are not live until you save.` });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error loading service for editing:', error);
      setFeedback({ type: 'error', message: 'Could not load this service for editing.' });
    }
  };

  const handleCloneService = async (serviceId) => {
    try {
      setIsCloningId(serviceId);
      await axios.post(apiUrl(`services/${serviceId}/clone/`), {}, { headers: authHeaders });
      await getAllData();
      setFeedback({ type: 'success', message: 'Service added to your list successfully.' });
      setActiveTab('my');
    } catch (error) {
      console.error('Error cloning service:', error);
      setFeedback({ type: 'error', message: 'Failed to add service. Please try again.' });
    } finally {
      setIsCloningId(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (step < 3) {
      if (validateStep(step)) {
        setStep((currentStep) => currentStep + 1);
      }
    }
  };

  const handleSaveService = async () => {
    if (!validateStep(3)) {
      setStep(3);
      setFeedback({ type: 'warning', message: 'Please resolve highlighted fields before saving.' });
      return;
    }

    try {
      setIsSaving(true);
      setFeedback({ type: 'info', message: '' });

      const servicePayload = {
        ...newService,
        category: getCategoryId(newService.category),
        attributes: attributes.map((attribute) => ({
          ...(attribute.id ? { id: attribute.id } : {}),
          name: attribute.name.trim(),
          input_type: attribute.input_type,
          required: Boolean(attribute.required),
          help_text: (attribute.help_text || '').trim(),
          placeholder: (attribute.placeholder || '').trim(),
          settings: attribute.settings || {},
          options: (attribute.input_type === 'range' ? buildRangeOptions(attribute) : (attribute.options || []))
            .map(normalizeOption)
            .filter((option) => option.value.trim())
            .map((option) => ({
              ...(option.id ? { id: option.id } : {}),
              value: option.value.trim(),
              color: attribute.input_type === 'color' ? option.color : '',
              extra_price: Number(option.extra_price || 0).toFixed(2),
              extra_duration_minutes: Math.max(0, Number(option.extra_duration_minutes || 0)),
            })),
        })),
      };

      if (editingServiceId) {
        await axios.patch(apiUrl(`services/${editingServiceId}/`), servicePayload, {
          headers: authHeaders,
        });
      } else {
        await axios.post(apiUrl('services/'), servicePayload, {
          headers: authHeaders,
        });
      }

      await getAllData();
      resetCreateForm();
      setActiveTab('my');
      setFeedback({
        type: 'success',
        message: editingServiceId
          ? 'Service changes saved successfully.'
          : 'Service saved successfully. It is now in your list.',
      });
    } catch (error) {
      console.error('Error saving service:', error?.response?.data || error.message);
      setFeedback({ type: 'error', message: 'Could not save service. Please review your details and retry.' });
    } finally {
      setIsSaving(false);
    }
  };

  const filteredServices = useMemo(() => {
    const source = activeTab === 'my' ? myServices : libraryServices;

    const filtered = source.filter((service) => {
      const nameMatch = service.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const categoryId = String(getCategoryId(service.category) || '');
      const categoryMatch = selectedCategory === 'all' || categoryId === String(selectedCategory);
      return nameMatch && categoryMatch;
    });

    return [...filtered].sort((a, b) => {
      if (sortBy === 'price-low') return Number(a.price) - Number(b.price);
      if (sortBy === 'price-high') return Number(b.price) - Number(a.price);
      if (sortBy === 'duration') return Number(a.duration) - Number(b.duration);
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [activeTab, libraryServices, myServices, searchTerm, selectedCategory, sortBy]);

  return (
    <div className="min-h-screen bg-[#F6F4F8] px-3 py-4 text-[#2E2E2E] sm:px-4 sm:py-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="rounded-2xl bg-gradient-to-r from-[#6D3B7E] to-[#8A4AA3] p-4 text-white shadow-lg sm:p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold sm:text-3xl">{isOwner ? 'Company Services' : 'My Services'}</h1>
              <p className="mt-2 text-sm text-purple-100 md:text-base">
                {isOwner
                  ? 'Create and organize the services clients can book with your company.'
                  : 'Build service offerings clients can understand quickly. Create from scratch or start from proven templates.'}
              </p>
            </div>

            <div className="grid gap-2 sm:flex sm:flex-wrap">
              <button
                type="button"
                onClick={() => {
                  setActiveTab('create');
                  setStep(1);
                }}
                className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-[#6D3B7E] hover:bg-purple-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                + Create Service
              </button>
              <button
                type="button"
                onClick={() => navigate(isOwner ? '/owner-dashboard' : '/worker-dashboard')}
                className="rounded-lg border border-purple-200 bg-transparent px-4 py-2 text-sm font-semibold text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                Back to {isOwner ? 'Owner Dashboard' : 'Dashboard'}
              </button>
            </div>
          </div>
        </header>

        <InlineFeedback type={feedback.type} message={feedback.message} />

        <section className="rounded-2xl bg-white p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[auto_1fr_1fr_auto] md:items-end">
            <label className="text-sm font-medium text-gray-700">
              View
              <select
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-[#6D3B7E] focus:outline-none focus:ring-2 focus:ring-[#E5D4ED]"
                value={activeTab}
                onChange={(event) => setActiveTab(event.target.value)}
              >
                <option value="my">{isOwner ? 'Company Services' : 'My Services'} ({myServices.length})</option>
                <option value="library">Service Library ({libraryServices.length})</option>
                <option value="create">Create / Edit Service</option>
              </select>
            </label>

            <label className="text-sm font-medium text-gray-700">
              Search
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search services by name"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-[#6D3B7E] focus:outline-none focus:ring-2 focus:ring-[#E5D4ED]"
              />
            </label>

            <label className="text-sm font-medium text-gray-700">
              Category
              <select
                value={selectedCategory}
                onChange={(event) => setSelectedCategory(event.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-[#6D3B7E] focus:outline-none focus:ring-2 focus:ring-[#E5D4ED]"
              >
                <option value="all">All categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="text-sm font-medium text-gray-700">
              Sort
              <select
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-[#6D3B7E] focus:outline-none focus:ring-2 focus:ring-[#E5D4ED]"
              >
                <option value="name">Name (A-Z)</option>
                <option value="price-low">Price (low to high)</option>
                <option value="price-high">Price (high to low)</option>
                <option value="duration">Duration (shortest first)</option>
              </select>
            </label>
          </div>
        </section>

        {activeTab === 'create' ? (
          <section className="rounded-2xl bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-[#4B2A56]">
                  {editingServiceId ? 'Edit service' : 'Create a service'}
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  {editingServiceId
                    ? 'Update the listing, booking questions, and client-facing image.'
                    : 'Build a polished listing clients can understand at a glance.'}
                </p>
              </div>
              <button
                type="button"
                onClick={resetCreateForm}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Reset form
              </button>
            </div>

            <StepIndicator currentStep={step} />

            <form className="mt-5 space-y-5" onSubmit={handleSubmit} noValidate>
              {step === 1 && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <label className="text-sm font-medium text-gray-700">
                    Service name
                    <input
                      type="text"
                      value={newService.name}
                      onChange={(event) => setServiceField('name', event.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                      placeholder="Ex: Knotless Braids"
                    />
                    <span className="mt-1 block text-xs text-red-600">{validationErrors.name}</span>
                  </label>

                  <label className="text-sm font-medium text-gray-700">
                    Category
                    <select
                      value={newService.category}
                      onChange={(event) => setServiceField('category', event.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                    >
                      <option value="">Select category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <span className="mt-1 block text-xs text-red-600">{validationErrors.category}</span>
                  </label>

                  <label className="text-sm font-medium text-gray-700 md:col-span-2">
                    Description
                    <textarea
                      value={newService.description}
                      onChange={(event) => setServiceField('description', event.target.value)}
                      className="mt-1 min-h-[96px] w-full rounded-lg border border-gray-300 px-3 py-2"
                      placeholder="Describe what the client should expect, including prep or aftercare notes."
                    />
                    <span className="mt-1 block text-xs text-red-600">{validationErrors.description}</span>
                  </label>

                  <div className="md:col-span-2">
                    <span className="text-sm font-medium text-gray-700">Service image</span>
                    <div className="mt-2 grid gap-3 rounded-2xl border border-dashed border-[#D8C0E1] bg-[#FBF8FD] p-3 sm:grid-cols-[160px_1fr] sm:items-center">
                      <div className="aspect-[4/3] overflow-hidden rounded-xl bg-gradient-to-br from-[#EEDFF4] to-[#D7BDE2]">
                        {newService.image_data ? (
                          <img
                            src={newService.image_data}
                            alt="Service preview"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center px-3 text-center text-sm font-medium text-[#6D3B7E]">
                            Add a photo clients will recognize
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">
                          Use a clear result photo or workspace image. It will be resized automatically for fast mobile loading.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <label className="cursor-pointer rounded-lg bg-[#6D3B7E] px-3 py-2 text-sm font-semibold text-white hover:bg-[#5A2F67]">
                            {isProcessingImage ? 'Optimizing…' : newService.image_data ? 'Replace photo' : 'Upload photo'}
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              onChange={handleImageUpload}
                              disabled={isProcessingImage}
                              className="sr-only"
                            />
                          </label>
                          {newService.image_data ? (
                            <button
                              type="button"
                              onClick={() => setServiceField('image_data', '')}
                              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-white"
                            >
                              Remove photo
                            </button>
                          ) : null}
                        </div>
                        <span className="mt-2 block text-xs text-red-600">{validationErrors.image_data}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <label className="text-sm font-medium text-gray-700">
                    Price (USD)
                    <input
                      type="number"
                      min="1"
                      value={newService.price}
                      onChange={(event) => setServiceField('price', event.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                      placeholder="120"
                    />
                    <span className="mt-1 block text-xs text-red-600">{validationErrors.price}</span>
                  </label>

                  <label className="text-sm font-medium text-gray-700">
                    Duration (minutes)
                    <input
                      type="number"
                      min="1"
                      value={newService.duration}
                      onChange={(event) => setServiceField('duration', event.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                      placeholder="90"
                    />
                    <span className="mt-1 block text-xs text-red-600">{validationErrors.duration}</span>
                  </label>

                  <label className="md:col-span-2 inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={newService.shared}
                      onChange={(event) => setServiceField('shared', event.target.checked)}
                    />
                    Share this service with other workers as a template.
                  </label>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="text-lg font-semibold text-[#4B2A56]">Custom attributes</h3>
                    <button
                      type="button"
                      onClick={addAttribute}
                      className="rounded-lg border border-[#D8C0E1] bg-[#F9F3FC] px-3 py-2 text-sm font-semibold text-[#6D3B7E] hover:bg-[#F2E8F8]"
                    >
                      + Add attribute
                    </button>
                  </div>

                  {attributes.length === 0 && (
                    <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-4 text-sm text-gray-600">
                      No attributes added yet. Add optional fields (like length, hair color, add-ons) to gather better booking details.
                    </p>
                  )}

                  {attributes.map((attribute, attributeIndex) => (
                    <div key={`attribute-${attributeIndex}`} className="rounded-xl border border-gray-200 p-4">
                      <div className="mb-3 flex items-center justify-between">
                        <p className="text-sm font-semibold text-[#4B2A56]">Attribute #{attributeIndex + 1}</p>
                        <button
                          type="button"
                          onClick={() => removeAttribute(attributeIndex)}
                          className="text-sm text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <label className="text-sm font-medium text-gray-700">
                          Attribute name
                          <input
                            type="text"
                            value={attribute.name}
                            onChange={(event) => updateAttribute(attributeIndex, 'name', event.target.value)}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                            placeholder="Ex: Hair length"
                          />
                          <span className="mt-1 block text-xs text-red-600">{validationErrors[`attribute-name-${attributeIndex}`]}</span>
                        </label>

                        <label className="text-sm font-medium text-gray-700">
                          Helper text <span className="font-normal text-gray-500">(optional)</span>
                          <input
                            type="text"
                            value={attribute.help_text || ''}
                            onChange={(event) => updateAttribute(attributeIndex, 'help_text', event.target.value)}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                            placeholder="Ex: Select the closest match"
                          />
                        </label>
                      </div>

                      <fieldset className="mt-4">
                        <legend className="text-sm font-medium text-gray-700">Input type</legend>
                        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                          {ATTRIBUTE_TYPES.map((type) => {
                            const isSelected = attribute.input_type === type.value;
                            return (
                              <button
                                key={type.value}
                                type="button"
                                onClick={() => updateAttribute(attributeIndex, 'input_type', type.value)}
                                aria-pressed={isSelected}
                                className={`rounded-xl border p-3 text-left transition ${
                                  isSelected
                                    ? 'border-[#6D3B7E] bg-[#F4EBF8] ring-2 ring-[#E5D4ED]'
                                    : 'border-gray-200 bg-white hover:border-[#CDB2D8] hover:bg-[#FCF9FD]'
                                }`}
                              >
                                <span className="block text-lg font-semibold text-[#6D3B7E]">{type.icon}</span>
                                <span className="mt-1 block text-sm font-semibold text-[#4B2A56]">{type.label}</span>
                                <span className="mt-0.5 block text-xs text-gray-500">{type.description}</span>
                              </button>
                            );
                          })}
                        </div>
                        <span className="mt-1 block text-xs text-red-600">{validationErrors[`attribute-type-${attributeIndex}`]}</span>
                      </fieldset>

                      <div className="mt-3 grid gap-3 md:grid-cols-2">
                        {['text', 'textarea', 'number'].includes(attribute.input_type) ? (
                          <label className="text-sm font-medium text-gray-700">
                            Placeholder <span className="font-normal text-gray-500">(optional)</span>
                            <input
                              type="text"
                              value={attribute.placeholder || ''}
                              onChange={(event) => updateAttribute(attributeIndex, 'placeholder', event.target.value)}
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              placeholder="Example answer"
                            />
                          </label>
                        ) : null}

                        <label className="flex min-h-11 items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={Boolean(attribute.required)}
                            onChange={(event) => updateAttribute(attributeIndex, 'required', event.target.checked)}
                            className="h-4 w-4 accent-[#6D3B7E]"
                          />
                          Require an answer before booking
                        </label>
                      </div>

                      {attribute.input_type === 'range' ? (
                        <div className="mt-3 rounded-xl bg-[#FAF8FC] p-3">
                          <p className="text-sm font-semibold text-[#4B2A56]">Slider settings</p>
                          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                            {[
                              ['min', 'Minimum', '0'],
                              ['max', 'Maximum', '10'],
                              ['step', 'Step', '1'],
                            ].map(([key, label, fallback]) => (
                              <label key={key} className="text-xs font-medium text-gray-600">
                                {label}
                                <input
                                  type="number"
                                  value={attribute.settings?.[key] ?? fallback}
                                  onChange={(event) => updateAttributeSetting(attributeIndex, key, event.target.value)}
                                  className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm"
                                />
                              </label>
                            ))}
                            <label className="text-xs font-medium text-gray-600">
                              Unit
                              <input
                                type="text"
                                value={attribute.settings?.unit || ''}
                                onChange={(event) => updateAttributeSetting(attributeIndex, 'unit', event.target.value)}
                                className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm"
                                placeholder="in, oz, level"
                              />
                            </label>
                          </div>
                          <span className="mt-1 block text-xs text-red-600">{validationErrors[`attribute-range-${attributeIndex}`]}</span>
                        </div>
                      ) : null}

                      {PRICED_SELECTOR_TYPES.includes(attribute.input_type) && (
                        <div className="mt-3 rounded-lg bg-[#FAF8FC] p-3">
                          <div className="mb-2 flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-700">
                                {attribute.input_type === 'color'
                                  ? 'Palette colors and adjustments'
                                  : attribute.input_type === 'range'
                                    ? 'Slider value adjustments'
                                    : ['toggle', 'checkbox'].includes(attribute.input_type)
                                      ? 'When selected'
                                      : 'Options and adjustments'}
                              </p>
                              <p className="text-xs text-gray-500">
                                Set an optional extra charge and extra appointment time for each client selection.
                              </p>
                            </div>
                            {OPTION_INPUT_TYPES.includes(attribute.input_type) ? (
                              <button
                                type="button"
                                onClick={() => addOption(attributeIndex)}
                                className="text-sm font-semibold text-[#6D3B7E] hover:text-[#4B2A56]"
                              >
                                + Add option
                              </button>
                            ) : null}
                          </div>

                          <div className="space-y-2">
                            {(attribute.input_type === 'range' ? buildRangeOptions(attribute) : (attribute.options || []).map(normalizeOption)).map((option, optionIndex) => (
                              <div key={`option-${attributeIndex}-${optionIndex}`} className="grid gap-2 rounded-xl border border-purple-100 bg-white p-3 sm:grid-cols-[minmax(140px,1fr)_130px_130px_auto] sm:items-end">
                                {attribute.input_type === 'color' ? (
                                  <input
                                    type="color"
                                    value={option.color || '#6D3B7E'}
                                    onChange={(event) => updateOption(attributeIndex, optionIndex, 'color', event.target.value)}
                                    className="h-11 w-12 cursor-pointer rounded-lg border border-gray-300 bg-white p-1"
                                    aria-label={`Color for option ${optionIndex + 1}`}
                                  />
                                ) : null}
                                <input
                                  type="text"
                                  value={option.value}
                                  onChange={(event) => updateOption(attributeIndex, optionIndex, 'value', event.target.value)}
                                  readOnly={attribute.input_type === 'range' || ['toggle', 'checkbox'].includes(attribute.input_type)}
                                  className="w-full rounded-lg border border-gray-300 px-3 py-2 read-only:bg-gray-50"
                                  placeholder={attribute.input_type === 'color' ? 'Ex: Jet black' : `Option ${optionIndex + 1}`}
                                />
                                <label className="text-xs font-medium text-gray-600">
                                  Extra charge ($)
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={option.extra_price ?? '0.00'}
                                    onChange={(event) => updateOption(attributeIndex, optionIndex, 'extra_price', event.target.value)}
                                    className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm"
                                  />
                                </label>
                                <label className="text-xs font-medium text-gray-600">
                                  Extra time (min)
                                  <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={option.extra_duration_minutes ?? 0}
                                    onChange={(event) => updateOption(attributeIndex, optionIndex, 'extra_duration_minutes', event.target.value)}
                                    className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm"
                                  />
                                </label>
                                {OPTION_INPUT_TYPES.includes(attribute.input_type) ? (
                                  <button
                                    type="button"
                                    onClick={() => removeOption(attributeIndex, optionIndex)}
                                    className="min-h-10 rounded-lg border border-gray-300 px-2 py-1 text-sm text-gray-600 hover:bg-gray-100"
                                    aria-label={`Remove option ${optionIndex + 1}`}
                                  >
                                    ✕
                                  </button>
                                ) : <span />}
                                <span className="text-xs text-red-600 sm:col-start-2">{validationErrors[`attribute-option-price-${attributeIndex}-${optionIndex}`]}</span>
                                <span className="text-xs text-red-600">{validationErrors[`attribute-option-duration-${attributeIndex}-${optionIndex}`]}</span>
                              </div>
                            ))}
                          </div>

                          <span className="mt-1 block text-xs text-red-600">{validationErrors[`attribute-options-${attributeIndex}`]}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={goToPreviousStep}
                  disabled={step === 1}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Back
                </button>

                <div className="flex gap-2">
                  {step < 3 ? (
                    <button
                      type="button"
                      onClick={goToNextStep}
                      className="rounded-lg bg-[#6D3B7E] px-4 py-2 text-sm font-semibold text-white hover:bg-[#5A2F67]"
                    >
                      Continue
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSaveService}
                      disabled={isSaving}
                      className="rounded-lg bg-[#10B981] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0F9A6D] disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {isSaving ? 'Saving...' : editingServiceId ? 'Save changes' : 'Save service'}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </section>
        ) : (
          <section className="space-y-4">
            {isLoadingPage ? (
              <div className="rounded-2xl bg-white p-6 text-sm text-gray-600 shadow-sm">Loading services...</div>
            ) : filteredServices.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-6 text-center shadow-sm">
                <h3 className="text-lg font-semibold text-[#4B2A56]">No services found</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Try a different search/filter, or create a new service to get started.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {filteredServices.map((service) => {
                  const serviceId = service.id;
                  const serviceAttributes = attributesByService[serviceId] || [];
                  const creatorId = typeof service.created_by === 'object'
                    ? service.created_by?.id
                    : service.created_by;
                  const canEdit = Number(creatorId) === Number(profile?.id);

                  return (
                    <article
                      key={serviceId}
                      className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                    >
                      {service.image_data ? (
                        <img
                          src={service.image_data}
                          alt=""
                          className="mb-4 aspect-[16/10] w-full rounded-xl object-cover"
                        />
                      ) : (
                        <div className="mb-4 flex aspect-[16/10] w-full items-center justify-center rounded-xl bg-gradient-to-br from-[#F1E5F5] to-[#DFC9E8] text-3xl text-[#6D3B7E]" aria-hidden="true">
                          ✦
                        </div>
                      )}
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-lg font-semibold text-[#4B2A56]">{service.name}</h3>
                        <span className="rounded-full bg-[#F5ECF9] px-2 py-1 text-xs font-semibold text-[#6D3B7E]">
                          {getCategoryName(service)}
                        </span>
                      </div>

                      <p className="mt-2 line-clamp-3 text-sm text-gray-600">{service.description || 'No description provided.'}</p>

                      <div className="mt-3 flex flex-wrap gap-2 text-xs">
                        <span className="rounded-full bg-[#EEF6FF] px-2 py-1 font-medium text-[#1B4D8A]">${service.price}</span>
                        <span className="rounded-full bg-[#F0FDF4] px-2 py-1 font-medium text-[#166534]">{service.duration} min</span>
                        <span className="rounded-full bg-[#F7F7F7] px-2 py-1 font-medium text-gray-700">
                          {service.shared ? 'Shared' : 'Private'}
                        </span>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        {canEdit ? (
                          <button
                            type="button"
                            onClick={() => handleEditService(service)}
                            className="rounded-lg bg-[#6D3B7E] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#5A2F67]"
                          >
                            Edit service
                          </button>
                        ) : null}

                        <button
                          type="button"
                          onClick={() => handleUseAsTemplate(serviceId)}
                          className="rounded-lg border border-[#D8C0E1] px-3 py-1.5 text-xs font-semibold text-[#6D3B7E] hover:bg-[#F9F3FC]"
                        >
                          Use as template
                        </button>

                        {activeTab === 'library' && (
                          <button
                            type="button"
                            onClick={() => handleCloneService(serviceId)}
                            disabled={isCloningId === serviceId}
                            className="rounded-lg bg-[#6D3B7E] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#5A2F67] disabled:cursor-not-allowed disabled:opacity-70"
                          >
                            {isCloningId === serviceId ? 'Adding...' : 'Add to my services'}
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() => fetchAttributesForService(serviceId)}
                          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          View details
                        </button>
                      </div>

                      {serviceAttributes.length > 0 && (
                        <div className="mt-4 rounded-lg bg-gray-50 p-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Attributes</p>
                          <ul className="mt-2 space-y-1 text-sm text-gray-700">
                            {serviceAttributes.map((attribute) => (
                              <li key={attribute.id || `${attribute.name}-${attribute.input_type}`}>
                                <span className="font-medium">{attribute.name}</span> ({attribute.input_type})
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default WorkerServices;
